import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  AcademicCapIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  StarIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CoachesList = ({ showAddForm, setShowAddForm }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(showAddForm || false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState(null);

  // React à la prop showAddForm
  React.useEffect(() => {
    if (showAddForm) {
      setIsFormOpen(true);
      setShowAddForm(false); // Reset pour éviter la réouverture
    }
  }, [showAddForm, setShowAddForm]);

  // Mock data - à remplacer par une vraie API
  const mockCoaches = [
    {
      id: 1,
      name: 'Ahmed Benali',
      photo: null,
      specialty: 'musculation',
      experience: 5,
      rating: 4.8,
      clients: 22,
      sessions: 128,
      status: 'active',
      email: 'ahmed.b@example.com',
      phone: '+213 555 123 456',
      bio: 'Coach certifié en musculation et préparation physique avec 5 ans d\'expérience',
      availability: ['Lundi', 'Mercredi', 'Vendredi'],
      salary: 80000,
      joinDate: '2022-04-15'
    },
    {
      id: 2,
      name: 'Fatima Rahmani',
      photo: null,
      specialty: 'fitness',
      experience: 3,
      rating: 4.6,
      clients: 18,
      sessions: 95,
      status: 'active',
      email: 'fatima.r@example.com',
      phone: '+213 555 789 012',
      bio: 'Spécialisée en fitness et remise en forme pour tous niveaux',
      availability: ['Mardi', 'Jeudi', 'Samedi'],
      salary: 75000,
      joinDate: '2022-08-22'
    },
    {
      id: 3,
      name: 'Karim Nadji',
      photo: null,
      specialty: 'cardio',
      experience: 7,
      rating: 4.9,
      clients: 26,
      sessions: 210,
      status: 'active',
      email: 'karim.n@example.com',
      phone: '+213 555 678 901',
      bio: 'Expert en entraînement cardio et haute intensité avec 7 ans d\'expérience',
      availability: ['Lundi', 'Mardi', 'Jeudi', 'Samedi'],
      salary: 90000,
      joinDate: '2021-06-10'
    }
  ];

  // Filtrage des coachs
  const filteredCoaches = mockCoaches
    .filter(coach => coach.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(coach => statusFilter === 'all' || coach.status === statusFilter)
    .filter(coach => specialtyFilter === 'all' || coach.specialty === specialtyFilter);

  // Spécialités disponibles pour le filtre
  const specialties = [
    { value: 'all', label: 'Toutes les spécialités' },
    { value: 'musculation', label: 'Musculation' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'crossfit', label: 'CrossFit' }
  ];

  // Statuts disponibles pour le filtre
  const statuses = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'active', label: 'Actif' },
    { value: 'vacation', label: 'En congé' },
    { value: 'inactive', label: 'Inactif' }
  ];

  // Gestion de l'ajout/modification de coach (mock)
  const handleSaveCoach = (coachData) => {
    console.log('Sauvegarde du coach:', coachData);
    // Appel API à implémenter
    setIsFormOpen(false);
  };

  // Gestion de la suppression de coach (mock)
  const handleDeleteCoach = (id) => {
    console.log('Suppression du coach avec ID:', id);
    // Appel API à implémenter
    setIsDeleteModalOpen(false);
    setCoachToDelete(null);
  };

  // Fonction pour ouvrir la modal de suppression
  const openDeleteModal = (coach) => {
    setCoachToDelete(coach);
    setIsDeleteModalOpen(true);
  };

  // Fonction pour ouvrir le formulaire de modification
  const openEditForm = (coach) => {
    setSelectedCoach(coach);
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Barre de filtres et recherche */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/5 backdrop-blur-sm rounded-xl p-4">
        <div className="w-full md:w-auto flex items-center px-3 py-2 bg-white/10 rounded-lg">
          <MagnifyingGlassIcon className="h-5 w-5 text-white/40" />
          <input
            type="text"
            className="ml-2 bg-transparent outline-none text-white w-full md:w-64"
            placeholder="Rechercher un coach..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <select
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
            className="px-3 py-2 bg-white/10 text-white rounded-lg outline-none"
          >
            {specialties.map((specialty) => (
              <option key={specialty.value} value={specialty.value} className="bg-slate-800">
                {specialty.label}
              </option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white/10 text-white rounded-lg outline-none"
          >
            {statuses.map((status) => (
              <option key={status.value} value={status.value} className="bg-slate-800">
                {status.label}
              </option>
            ))}
          </select>

          <button 
            onClick={() => {
              setSelectedCoach(null);
              setIsFormOpen(true);
            }}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-1"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Liste des coachs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCoaches.map((coach) => (
          <motion.div
            key={coach.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/30 transition-all duration-300"
          >
            <div className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  {coach.photo ? (
                    <img 
                      src={coach.photo} 
                      alt={coach.name} 
                      className="h-16 w-16 rounded-full object-cover border-2 border-blue-500/30" 
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                      <AcademicCapIcon className="h-8 w-8 text-white/70" />
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-lg font-medium text-white">{coach.name}</h3>
                    <p className="text-white/60 text-sm">
                      {specialties.find(s => s.value === coach.specialty)?.label || coach.specialty}
                    </p>
                    <div className="flex items-center mt-1">
                      <StarIcon className="h-4 w-4 text-yellow-400" />
                      <span className="ml-1 text-white/70 text-sm">{coach.rating}</span>
                      <span className="mx-2 text-white/30">•</span>
                      <span className="text-white/70 text-sm">{coach.experience} ans exp.</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button 
                    onClick={() => openEditForm(coach)}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4 text-white/70" />
                  </button>
                  <button 
                    onClick={() => openDeleteModal(coach)}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 text-white/70 hover:text-red-400" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 text-blue-400" />
                  <span className="ml-2 text-white/70 text-sm">{coach.clients} clients</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 text-green-400" />
                  <span className="ml-2 text-white/70 text-sm">{coach.sessions} séances</span>
                </div>
              </div>

              <div className="mt-4 text-sm text-white/60 line-clamp-2">
                {coach.bio}
              </div>

              <div className="mt-4 flex justify-end">
                <button 
                  onClick={() => setSelectedCoach(coach)}
                  className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Voir détails
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredCoaches.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <AcademicCapIcon className="h-12 w-12 text-white/20" />
          <p className="mt-4 text-white/50 text-lg">Aucun coach ne correspond à votre recherche</p>
          <button 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setSpecialtyFilter('all');
            }}
            className="mt-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}

      {/* Formulaire d'ajout/modification (modal) */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                {selectedCoach ? 'Modifier le coach' : 'Ajouter un coach'}
              </h2>
              <button 
                onClick={() => setIsFormOpen(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Formulaire à implémenter */}
              <p className="text-white/70">Formulaire d'ajout/modification à implémenter</p>
              
              <div className="flex justify-end mt-6 gap-2">
                <button 
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleSaveCoach({...selectedCoach})}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {isDeleteModalOpen && coachToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <TrashIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Supprimer ce coach</h3>
              <p className="text-white/70 mb-6">
                Êtes-vous sûr de vouloir supprimer le coach {coachToDelete.name} ? Cette action est irréversible.
              </p>
              <div className="flex gap-3 w-full">
                <button 
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleDeleteCoach(coachToDelete.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail du coach */}
      {selectedCoach && !isFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                Détails du coach
              </h2>
              <button 
                onClick={() => setSelectedCoach(null)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 mb-6">
              {selectedCoach.photo ? (
                <img 
                  src={selectedCoach.photo} 
                  alt={selectedCoach.name} 
                  className="h-24 w-24 rounded-full object-cover border-2 border-blue-500/30" 
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                  <AcademicCapIcon className="h-12 w-12 text-white/70" />
                </div>
              )}
              
              <div>
                <h3 className="text-xl font-medium text-white">{selectedCoach.name}</h3>
                <p className="text-white/60">
                  {specialties.find(s => s.value === selectedCoach.specialty)?.label || selectedCoach.specialty}
                </p>
                <div className="flex items-center mt-1">
                  <StarIcon className="h-4 w-4 text-yellow-400" />
                  <span className="ml-1 text-white/70">{selectedCoach.rating}</span>
                  <span className="mx-2 text-white/30">•</span>
                  <span className="text-white/70">{selectedCoach.experience} ans d'expérience</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Email</p>
                <p className="text-white">{selectedCoach.email}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Téléphone</p>
                <p className="text-white">{selectedCoach.phone}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Date d'embauche</p>
                <p className="text-white">{new Date(selectedCoach.joinDate).toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Salaire</p>
                <p className="text-white">{selectedCoach.salary.toLocaleString('fr-DZ')} DA / mois</p>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-2">Bio</h4>
              <p className="text-white/70">{selectedCoach.bio}</p>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-2">Disponibilité</h4>
              <div className="flex flex-wrap gap-2">
                {selectedCoach.availability.map((day) => (
                  <span key={day} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                    {day}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => {
                  setSelectedCoach(null);
                  openEditForm(selectedCoach);
                }}
                className="px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10 flex items-center gap-1"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Modifier</span>
              </button>
              <button 
                onClick={() => {
                  setSelectedCoach(null);
                  openDeleteModal(selectedCoach);
                }}
                className="px-4 py-2 bg-red-600/30 text-red-300 rounded-lg hover:bg-red-600/50 flex items-center gap-1"
              >
                <TrashIcon className="h-4 w-4" />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachesList; 